import { ReactElement } from 'react';
import { isEmpty } from '@client/common/utils/empty/empty';
import { useTranslate } from '@client/i18n/hooks';
import { ProductAccessories } from '@client/product/components/ProductDetails/ProductAccessories';
import { ProductDescription } from '@client/product/components/ProductDetails/ProductDescription/ProductDescription';
import { ProductDocuments } from '@client/product/components/ProductDetails/ProductDocuments';
import { ProductSpecification } from '@client/product/components/ProductDetails/ProductSpecification';
import { ProductVariantsMatrix } from '@client/product/components/ProductDetails/ProductVariantsMatrix';
import { useProductAccessoriesEffect, useProductVariantsMatrixEffect } from '@client/product/hooks';
import { ProductAccessoryCategoryType } from '@client/product/redux/productAccessories/types';
import { Badge } from 'b2b-components/theme/components/Badge';
import { Tab } from 'b2b-components/theme/components/Tabs';
import { ProductDetails } from './ProductDetails';
import { Props } from './ProductDetailsContainer.types';

export const ProductDetailsContainer = ({
  description,
  packaging,
  sku,
  isInkFinderLinkDisplayed,
}: Props): ReactElement => {
  const t = useTranslate();
  const accessories = useProductAccessoriesEffect(sku);
  const variantsMatrix = useProductVariantsMatrixEffect(sku);
  const columnLeft = [];
  const columnRight = [];

  if (
    description
    && (description.subtitle !== ''
      || description.lead !== ''
      || (Array.isArray(description.dangerDetails)
        && description.dangerDetails.length > 0)
      || (Array.isArray(description.dangerIcons)
        && description.dangerIcons.length > 0)
      || (Array.isArray(description.paragraphs)
        && description.paragraphs.length > 0)
      || (Array.isArray(description.banners)
        && description.banners.length > 0)
      || (Array.isArray(description.remarks) && description.remarks.length > 0))
  ) {
    columnLeft.push(
      <Tab title={t('Description')} key='description' dataAttributes={{ 'data-testid': 'pdpDescriptionTab' }}>
        <ProductDescription description={description} />
      </Tab>,
    );
  }

  //accessories are always in the right column
  if (
    accessories.isLoaded
    && !isEmpty(accessories.data)
    && accessories.data
  ) {
    const accessoriesCount = accessories.data.reduce(
      (sum, category: ProductAccessoryCategoryType) => sum + category.count,
      0,
    );
    const label = (
      <>
        <span>{t('Accessories')}</span>
        <Badge>{accessoriesCount}</Badge>
      </>
    );

    columnRight.push(
      <Tab title={label} key='accessories' dataAttributes={{ 'data-testid': 'pdpAccessoriesTab' }}>
        <ProductAccessories sku={sku} />
      </Tab>,
    );
  }

  //Specifications may be in any of columns
  if (description.specification) {
    const component = (
      <Tab title={t('Specification')} key='specification' dataAttributes={{ 'data-testid': 'pdpSpecificationTab' }}>
        <ProductSpecification specification={description.specification} packaging={packaging} />
      </Tab>
    );

    if (columnRight.length) {
      columnLeft.push(component);
    } else {
      columnRight.push(component);
    }
  }

  //documents are always in the left column
  if (
    description
    && (description.documents.regular.length > 0
      || description.documents.danger.length > 0)
  ) {
    const label = (
      <>
        <span>{t('Documents')}</span>
        <Badge>
          {description.documents.danger.length
            + description.documents.regular.length}
        </Badge>
      </>
    );

    columnLeft.push(
      <Tab title={label} key='documents' dataAttributes={{ 'data-testid': 'pdpDocumentsTab' }}>
        {description.documents.regular.length > 0 && (
          <ProductDocuments documents={description.documents.regular} />
        )}
        {description.documents.danger.length > 0 && (
          <ProductDocuments
            documents={description.documents.danger}
            label={t('Danger documents')}
          />
        )}
      </Tab>,
    );
  }

  //variants matrix are always in the right column
  if (
    variantsMatrix.isLoaded
    && !isEmpty(variantsMatrix.data)
    && variantsMatrix.data
  ) {
    columnRight.push(
      <Tab title={t('Variants')} key='variants' dataAttributes={{ 'data-testid': 'pdpVariantsTab' }}>
        <ProductVariantsMatrix variantsMatrix={variantsMatrix.data} />
      </Tab>,
    );
  }

  return (
    <ProductDetails
      columnLeft={columnLeft}
      columnRight={columnRight}
      isInkFinderLinkDisplayed={isInkFinderLinkDisplayed}
    />
  );
};
